import Phaser from 'phaser';
import { Direction, DIRECTION_DELTA, GridCell } from '../types';
import { MazeGrid } from './MazeGrid';
import { SoundManager } from './SoundManager';
import { findPath } from '../utils/pathfinder';
import { PLAYER_TWEEN_DURATION, CELL_SIZE, COLORS } from '../utils/constants';

const HERO_TEX_KEY = '__hero';

function ensureHeroTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HERO_TEX_KEY)) return;

  const s = Math.round(CELL_SIZE * 0.9);
  const g = scene.add.graphics();
  const cx = s / 2;

  // --- Helmet / head ---
  const headR = s * 0.3;
  const headY = s * 0.28;
  // Helmet (slightly larger circle behind)
  g.fillStyle(0x2266aa, 1);
  g.fillCircle(cx, headY, headR + 1);
  // Face visor slit
  g.fillStyle(0x003366, 1);
  g.fillRect(cx - headR * 0.55, headY - headR * 0.15, headR * 1.1, headR * 0.3);
  // Eyes (bright cyan)
  g.fillStyle(0x66eeff, 1);
  const eyeOff = headR * 0.32;
  g.fillCircle(cx - eyeOff, headY, headR * 0.15);
  g.fillCircle(cx + eyeOff, headY, headR * 0.15);

  // --- Body / armor ---
  const bodyTop = headY + headR * 0.6;
  const bodyW = s * 0.5;
  const bodyH = s * 0.3;
  g.fillStyle(0x0088dd, 1);
  g.fillRect(cx - bodyW / 2, bodyTop, bodyW, bodyH);
  // Belt
  g.fillStyle(0x886622, 1);
  g.fillRect(cx - bodyW / 2, bodyTop + bodyH - 3, bodyW, 3);
  // Chest emblem (small diamond)
  g.fillStyle(0x66eeff, 1);
  const embY = bodyTop + bodyH * 0.35;
  const embS = s * 0.06;
  g.fillTriangle(cx, embY - embS, cx + embS, embY, cx, embY + embS);
  g.fillTriangle(cx, embY - embS, cx - embS, embY, cx, embY + embS);

  // --- Legs ---
  const legTop = bodyTop + bodyH;
  const legW = bodyW * 0.3;
  const legH = s * 0.18;
  g.fillStyle(0x005599, 1);
  g.fillRect(cx - bodyW / 2 + 2, legTop, legW, legH);
  g.fillRect(cx + bodyW / 2 - legW - 2, legTop, legW, legH);

  // --- Shield (left side) ---
  g.fillStyle(0x4488cc, 1);
  const shX = cx - bodyW / 2 - 3;
  const shY = bodyTop + 2;
  const shW = 5;
  const shH = bodyH * 0.7;
  g.fillRect(shX, shY, shW, shH);
  g.fillTriangle(shX, shY + shH, shX + shW, shY + shH, shX + shW / 2, shY + shH + 3);

  // --- Sword (right side) ---
  g.fillStyle(0xcccccc, 1);
  const swX = cx + bodyW / 2 + 1;
  g.fillRect(swX, bodyTop - 4, 2, bodyH + 4);
  // Sword hilt
  g.fillStyle(0x886622, 1);
  g.fillRect(swX - 2, bodyTop + bodyH * 0.2, 6, 3);
  // Blade tip
  g.fillStyle(0xeeeeee, 1);
  g.fillTriangle(swX, bodyTop - 4, swX + 2, bodyTop - 4, swX + 1, bodyTop - 8);

  g.generateTexture(HERO_TEX_KEY, s, s);
  g.destroy();
}

export class Player {
  readonly sprite: Phaser.GameObjects.Sprite;
  gridRow: number;
  gridCol: number;
  isMoving: boolean = false;
  autoMode: boolean = false;

  private scene: Phaser.Scene;
  private maze: MazeGrid;
  private soundManager: SoundManager | null;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private lastDirection: Direction = Direction.NONE;
  private keyW!: Phaser.Input.Keyboard.Key;
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyS!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;

  // Auto-pilot state
  private autoPath: GridCell[] = [];
  private autoPathIndex: number = 0;
  private autoRecalcCounter: number = 0;

  constructor(scene: Phaser.Scene, maze: MazeGrid, soundManager?: SoundManager) {
    this.scene = scene;
    this.maze = maze;
    this.soundManager = soundManager ?? null;
    this.gridRow = maze.startCell.row;
    this.gridCol = maze.startCell.col;

    const pos = maze.cellToPixel(this.gridRow, this.gridCol);
    ensureHeroTexture(scene);
    this.sprite = scene.add.sprite(pos.x, pos.y, HERO_TEX_KEY);
    this.sprite.setScale(0.75);
    this.sprite.setDepth(10);

    if (scene.input.keyboard) {
      this.cursors = scene.input.keyboard.createCursorKeys();
      this.keyW = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W);
      this.keyA = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
      this.keyS = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S);
      this.keyD = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    }
  }

  update(): void {
    if (this.isMoving) return;

    const direction = this.autoMode
      ? this.getAutoDirection()
      : this.getInputDirection();
    if (direction === Direction.NONE) return;

    if (!this.maze.isPassable(this.gridRow, this.gridCol, direction)) return;

    const delta = DIRECTION_DELTA[direction];
    const targetRow = this.gridRow + delta.row;
    const targetCol = this.gridCol + delta.col;
    const targetPos = this.maze.cellToPixel(targetRow, targetCol);

    this.isMoving = true;
    this.soundManager?.playFootstep();
    this.scene.tweens.add({
      targets: this.sprite,
      x: targetPos.x,
      y: targetPos.y,
      duration: PLAYER_TWEEN_DURATION,
      ease: 'Linear',
      onComplete: () => {
        this.gridRow = targetRow;
        this.gridCol = targetCol;
        this.isMoving = false;
      },
    });
  }

  private getAutoDirection(): Direction {
    this.autoRecalcCounter++;
    if (this.autoRecalcCounter % 10 === 0 || this.autoPath.length === 0 || this.autoPathIndex >= this.autoPath.length) {
      this.autoPath = findPath(
        this.maze.cells, this.maze.height, this.maze.width,
        { row: this.gridRow, col: this.gridCol },
        this.maze.exitCell,
      );
      this.autoPathIndex = this.autoPath.length > 1 ? 1 : 0;
    }

    if (this.autoPath.length === 0 || this.autoPathIndex >= this.autoPath.length) {
      return Direction.NONE;
    }

    const next = this.autoPath[this.autoPathIndex];
    this.autoPathIndex++;

    const dr = next.row - this.gridRow;
    const dc = next.col - this.gridCol;
    if (dr === -1) return Direction.UP;
    if (dr === 1) return Direction.DOWN;
    if (dc === -1) return Direction.LEFT;
    if (dc === 1) return Direction.RIGHT;
    return Direction.NONE;
  }

  private getInputDirection(): Direction {
    const up = this.cursors.up.isDown || this.keyW?.isDown;
    const down = this.cursors.down.isDown || this.keyS?.isDown;
    const left = this.cursors.left.isDown || this.keyA?.isDown;
    const right = this.cursors.right.isDown || this.keyD?.isDown;

    if (up) this.lastDirection = Direction.UP;
    if (down) this.lastDirection = Direction.DOWN;
    if (left) this.lastDirection = Direction.LEFT;
    if (right) this.lastDirection = Direction.RIGHT;

    switch (this.lastDirection) {
      case Direction.UP: return up ? Direction.UP : Direction.NONE;
      case Direction.DOWN: return down ? Direction.DOWN : Direction.NONE;
      case Direction.LEFT: return left ? Direction.LEFT : Direction.NONE;
      case Direction.RIGHT: return right ? Direction.RIGHT : Direction.NONE;
      default: return Direction.NONE;
    }
  }

  destroy(): void {
    this.sprite.destroy();
  }
}
